import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { route } from '@services/symfony';
import { gsspGetLocale } from '@utils/gssp';
import { fetcher } from 'src/utils/data-fetching';

import { SanitizeHTML } from '@components/common/SanitizeHTML/SanitizeHTML';
import {
  ChatTypingDots, Button, Avatar, TailwindSpinner,
} from '@components/common';
import {
  useEffect, useRef, useState, useMemo, useCallback,
} from 'react';

import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { ChatTextarea } from '@components/common/Chat/ChatTextarea';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { Message, useStreamState } from '@components/templates/bia/hooks/useStreamState';
import { v4 as uuidv4 } from 'uuid';
import { LayoutWithHeader } from '@components/layout/Layouts/LayoutWithHeader';
import type { JSX } from 'react';
import { ChatMessageComponent } from '@components/templates/homepage/chat/ChatMessageComponent';
import { ChatJobModal } from '@components/templates/homepage/chat/ChatJobModal';
import { Footer } from '@components/layout';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';
import { useAppSelector } from '@store/index';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import NiceModal from '@ebay/nice-modal-react';
import { BulkApplyModal } from '@components/templates/job/apply/BulkApplyModal';
import { sanitizeHtml } from '@utils/strings/sanitizeHtml';
import { StartAiInterviewChatButton } from '@components/templates/ai-interview';
import bestie from '@images/agent/bestie-white-bg.png';
import SuccessStoriesCarousel from '@root/src/components/templates/homepage/SuccessStoriesCarousel';
import { Transition } from '@headlessui/react';
import ChatTalentModal from '@root/src/components/templates/homepage/chat/ChatTalentModal';

const CHAT_API = `${process.env.NEXT_PUBLIC_AI_URL}/chat`;

// Interface
// ************************************************************************************
interface HomepageProps {
  locale: Locale;
  successStories: ApiSchema<'SuccessStoriesResponse'>;
}

export interface JobDataset {
  items: {
    id: string;
    score: number;
    data: ApiSchema<'JobItem'>
  }[]
}

/**
 * @description Homepage component
 * - **locale** - The locale of the app
 * - **eventsFeedServer** - The events list from GSSP
 * - **tagCloud** - The tag cloud list from GSSP
 * - **timestamp** - The timestamp for the events feed API call (in seconds)
 */
const Index = (props: HomepageProps) => {
  // Destructure props
  const { locale, successStories } = props;

  // The following script tag contains JSON-LD (JavaScript Object Notation for Linked Data)
  // structured data with the application/ld+json MIME type. JSON-LD is used to provide
  // machine-readable information about the website, enhancing its presentation
  // in search engine results and enabling better indexing by search engines.
  const ORG_SCHEMA = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: 'https://www.bestjobs.eu/',
    name: 'BestJobs',
    // SiteLinks Search Box
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `https://www.bestjobs.eu${route('app_job_canonical')}/{search_term_string}?utm_source=google&utm_medium=organic%20search`,
      },
      'query-input': 'required name=search_term_string',
    },
  });

  const agent = {
    id: '', // leave blank in order to use unified chat agent (hr + search)
  };

  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();

  const {
    stream, startStream, initializeWithHistory, isLoadingHistory,
  } = useStreamState(CHAT_API);
  const [input, setInput] = useState('');
  const wasInitialised = useRef(false);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const initialMessageFromUrlSent = useRef(false);
  const [previousSentMessagesIndex, setPreviousSentMessagesIndex] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const anonSessionId = useAnonSessionId();
  const [successStoriesAutoplay, setSuccessStoriesAutoplay] = useState(false);
  const [talentsNavigationList, setTalentsNavigationList] = useState<ApiSchema<'ResumeCardData'>[]>([]);

  const headers = useMemo(() => ({
    'x-anon-session-id': anonSessionId,
  }), [anonSessionId]);

  useEffect(() => {
    if (!initialMessageFromUrlSent.current && typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const initialQuery = searchParams.get('q');
      if (initialQuery) {
        const message = {
          id: uuidv4(),
          content: initialQuery,
          type: 'human',
        };

        const callBack = () => startStream(message, agent.id || '', {
          headers,
          configurations: { user_locale: locale || 'ro' },
        });

        if (!wasInitialised.current) {
          void initializeWithHistory(agent.id, '', callBack);
          wasInitialised.current = true;
        } else {
          void callBack();
        }

        router.replace('/', { scroll: false });

        initialMessageFromUrlSent.current = true;
      }
    }
  }, [router, locale, startStream, agent.id, headers, initializeWithHistory]);

  const showWelcomeBack = useCallback(
    (lastMessageDate: Date) => {
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);

      if (lastMessageDate > oneDayAgo) {
        // Don't do anything if the last message was within the last 24 hours
        return;
      }
      const config = { user_locale: locale || 'ro' };
      void startStream(
        { id: uuidv4(), content: '', type: 'human' },
        agent.id || '',
        { configurations: config },
      );
    },
    [locale, agent.id, startStream],
  );


  useEffect(() => {
    if (!wasInitialised.current) {
      void initializeWithHistory(agent.id).then((response) => {
        if (response.created_at) {
          showWelcomeBack(new Date(response.created_at));
        }
      });

      wasInitialised.current = true;
    }
  }, [agent.id, initializeWithHistory, showWelcomeBack]);

  useEffect(() => {
    if (stream?.messages && stream?.messages?.length > 0 && typeof window !== 'undefined') {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }, [stream?.messages]); // Runs whenever content

  const handleSubmit = async (event?: { preventDefault?: () => void }) => {
    event?.preventDefault?.();

    if (!sanitizeHtml(input)) return;

    const message = {
      id: uuidv4(),
      content: input,
      type: 'human',
    };
    setInput('');

    try {
      await startStream(message, agent.id || '', {
        headers,
        configurations: { user_locale: locale || 'ro' },
      });
    } catch (error) {
      console.error('Error sending message', error);
    }
  };

  const handleChatInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setInput(e.target.value);
  };

  // When the user presses enter, submit the message
  const handleChatInputEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    void handleSubmit();
    setPreviousSentMessagesIndex(null); // reset the index
  };

  // When the user presses shift + enter, add a new line in the textarea
  const handleChatInputShiftEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setInput(`${input}\n`);
    if (textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  };

  // When the user presses arrow up, search up through the previous sent messages
  const handleChatInputArrowUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    // get all human messages - previous sent messages
    const humanMessages = stream?.messages?.filter((msg) => msg.type === 'human') || [];
    if (previousSentMessagesIndex === null && humanMessages.length) {
      // index is null, set it to the last message
      setPreviousSentMessagesIndex(humanMessages.length - 1);
      setInput(humanMessages[humanMessages.length - 1].content);
    } else if (previousSentMessagesIndex !== null && previousSentMessagesIndex > 0) {
      // index is not null, decrement it
      setPreviousSentMessagesIndex(previousSentMessagesIndex - 1);
      setInput(humanMessages[previousSentMessagesIndex - 1].content);
    }
  };

  // When the user presses arrow down, search down through the previous sent messages
  const handleChatInputArrowDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    // get all human messages - previous sent messages
    const humanMessages = stream?.messages?.filter((msg) => msg.type === 'human') || [];
    if (previousSentMessagesIndex !== null && previousSentMessagesIndex < humanMessages.length - 1) {
      // index is not null, increment it
      setPreviousSentMessagesIndex(previousSentMessagesIndex + 1);
      setInput(humanMessages[previousSentMessagesIndex + 1].content);
    } else {
      // index is the last message, reset it
      setPreviousSentMessagesIndex(null);
      setInput('');
    }
  };

  // Handle keydown events
  const handleChatInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleChatInputEnter(e);
    } else if (e.key === 'Enter' && e.shiftKey) {
      handleChatInputShiftEnter(e);
    } else if (e.key === 'ArrowUp') {
      handleChatInputArrowUp(e);
    } else if (e.key === 'ArrowDown') {
      handleChatInputArrowDown(e);
    }
  };

  const jobsArray = useMemo(() => stream?.messages
    ?.filter((item) => (item?.response_metadata?.customData?.jobSearchResults?.items
      && item?.response_metadata?.customData?.jobSearchResults?.items?.length > 0))
    .reduce<{ items: ApiSchema<'JobItem'>[] | undefined }[]>((acc, item) => {
    acc.push({ items: item?.response_metadata?.customData?.jobSearchResults?.items?.map((i) => i.data) });
    return acc;
  }, []), [stream?.messages]);

  const formElements = (
    <div className="flex flex-col rounded-3xl border-6 border-surface-50 transition-all duration-300 focus-within:border-primary-light/10">
      <div className="rounded-2xl border border-input bg-surface p-4 pb-2">
        <ChatTextarea
          textareaRef={textareaRef}
          input={input}
          handleInputChange={handleChatInputChange}
          handleKeyDown={handleChatInputKeyDown}
          placeholder={t('landing.bjaichat.input')}
        />
        <div className="flex items-center justify-between">
          <StartAiInterviewChatButton
            onMouseDown={(e) => e.preventDefault()}
          />
          <span />
          <Button
            type="button"
            color="secondary"
            rounding="full"
            size="sm"
            className="shrink-0 !p-3"
            onClick={() => {
              void handleSubmit();
            }}
          >
            <PaperAirplaneIcon className="size-4 text-white" />
          </Button>
        </div>
      </div>
    </div>
  );

  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);

  useEffect(() => {
    if (bulkApplyFeed?.length) {
      void NiceModal.show(BulkApplyModal, { jobList: bulkApplyFeed, locale });
    }
  }, [bulkApplyFeed, locale]);

  const onCvDetailOpen = useCallback((message: Message) => {
    setTalentsNavigationList(message.response_metadata?.customData?.resumeSearchResults?.items || []);
  }, []);

  const messagesList = useMemo(() => {
    if (!stream?.messages) return [];

    // Use a Map to remove duplicates based on message id
    return [...new Map(stream.messages.map((m) => [m.id, m])).values()]
      .reverse()
      .map((m, index) => (
        <div key={`message-container-${m.id}`} className="flex flex-col gap-4">
          <ChatMessageComponent
            key={`message-${m.id}`}
            message={{ ...m }}
            ref={index === 0 ? lastMessageRef : null}
            locale={locale}
            onInteraction={onCvDetailOpen}
          />
        </div>
      ));
  }, [stream?.messages, locale, onCvDetailOpen]);

  // Render page
  // ******************************
  return (
    <>
      {/* HEAD - title, meta, etc */}
      <Head>
        <title>
          {t('homepage.react.meta.title')}
        </title>
        <meta name="description" content={t('meta.homepage.description_react')} />
        <SanitizeHTML as="script" type="application/ld+json" htmlContent={ORG_SCHEMA} />
      </Head>


      {/* PAGE CONTENT */}
      { isLoadingHistory
        ? (
          <div className="flex size-full grow items-center justify-center bg-surface-100">
            <TailwindSpinner className="size-5 text-secondary" />
          </div>
        ) : (
          <div className="flex grow flex-col bg-surface-100">
            {stream?.messages && stream.messages?.length > 0 ? (
              <div className="flex h-full grow rounded">
                <div
                  className="container mx-auto flex h-full flex-col items-center overflow-y-hidden rounded p-4"
                >
                  <div
                    className="flex size-full grow flex-col-reverse gap-5 pb-44 md:pb-32"
                    ref={messageContainerRef}
                  >
                    {stream?.status === 'inflight' && <ChatTypingDots />}
                    { messagesList }
                  </div>

                  <ChatJobModal locale={locale} jobsArray={jobsArray} />
                  <ChatTalentModal talentsArray={[{ items: talentsNavigationList }]} />
                  <form
                    onSubmit={() => handleSubmit}
                    className="fixed bottom-16 left-1/2 z-[5] w-11/12 max-w-3xl -translate-x-1/2 transition-all duration-300 focus-within:max-w-4xl md:bottom-5"
                  >
                    {formElements}
                  </form>
                </div>
              </div>
            ) : (
              <>
                <div className="container h-screen w-full grow py-2 md:py-8">
                  <div className="grid w-full grid-cols-5 gap-2 md:gap-4">
                    <div className="col-span-5 flex items-start gap-2">
                      <Transition
                        as="div"
                        show
                        appear
                        enter="transition-all duration-400"
                        enterFrom="opacity-0 -translate-y-8"
                        enterTo="opacity-100 translate-y-0"
                      >
                        <Avatar imgPath={bestie.src} name="Bestie" className="size-18 md:size-20" />
                      </Transition>
                      <div className="flex flex-col gap-1 pt-8">
                        <Transition
                          as="div"
                          show
                          appear
                          enter="transition-all duration-400 delay-[800ms]"
                          enterFrom="opacity-0 -translate-y-8"
                          enterTo="opacity-100 translate-y-0"
                        >
                          <ChatMessageComponent
                            message={{
                              id: 'bestie-greet',
                              type: 'bestie-greet',
                              content: sanitizeHtml(t('homepage.bestie.greet')),
                            }}
                            locale={locale}
                          />
                        </Transition>
                        <Transition
                          as="div"
                          show
                          appear
                          enter="transition-all duration-400 delay-[1600ms]"
                          enterFrom="opacity-0 -translate-y-8"
                          enterTo="opacity-100 translate-y-0"
                        >
                          <ChatMessageComponent
                            message={{
                              id: 'bestie-value-proposition',
                              type: 'bestie-value-proposition',
                              content: sanitizeHtml(t('homepage.bestie.value.proposition')),
                            }}
                            locale={locale}
                          />
                        </Transition>
                      </div>
                    </div>
                    <Transition
                      as="div"
                      show
                      appear
                      className="col-span-5"
                      enter="transition-all duration-400 delay-[2400ms]"
                      enterFrom="opacity-0 -translate-y-8"
                      enterTo="opacity-100 translate-y-0"
                      afterEnter={() => setSuccessStoriesAutoplay(true)}
                    >
                      <SuccessStoriesCarousel
                        items={successStories?.items ?? []}
                        sliderSettings={{ autoplay: successStoriesAutoplay }}
                      />
                    </Transition>
                  </div>
                  <div className="container fixed inset-x-0 bottom-12 z-[5] my-4 px-4 md:bottom-0 md:mt-6">
                    <form
                      onSubmit={() => handleSubmit}
                      className="mx-auto max-w-3xl transition-all duration-300 focus-within:max-w-4xl lg:w-8/12 focus-within:lg:w-10/12"
                    >
                      {formElements}
                    </form>
                  </div>
                </div>
                <Footer className="pb-28 md:pb-35" />
              </>
            )}
          </div>
        )}
    </>
  );
};

/**
 * @description Get the layout for the page
 * @param page
 */
Index.getLayout = function getLayout(page: JSX.Element) {
  return (<LayoutWithHeader>{page}</LayoutWithHeader>);
};


/**
 * @description Get the server-side props
 * @param context - The context object
 */
export const getServerSideProps: GetServerSideProps = async (context) => {
  // Props destructuring
  const { params, req } = context;

  const locale = gsspGetLocale(params);

  const [
    successStories,
  ] = await Promise.all([
    fetcher<ApiSchema<'SuccessStoriesResponse'>>('/v1/success-stories', locale, {
      ssrClientHeaders: req.headers,
    }),
  ]);

  // Return the props
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      locale,
      successStories,
    },
  };
};


/**
 * @description Export the page component.
 */
export default Index;
